import { createSelector } from 'reselect';
import { canUseDOM } from 'exenv';
import { decodeSearchParams } from '../../../libs/utils';
import { featuresSelector } from '../../selectors/global';
import { safeParseInt } from '../../../../common/utils';
import { DEFAULT_GLOBAL_SEARCH_PAGE_SIZE } from '../constants';
import _ from 'lodash';
import { SearchFiltersType } from '../types';

export const globalSearchSelector = (state: any): any => state.globalSearch;

export const qParamsSelector = () => (canUseDOM ? decodeSearchParams(window.location.search.substr(1)) : {});

export const searchTypeSelector = createSelector(
  globalSearchSelector,
  qParamsSelector,
  ({ selectedType }, { type }) => (selectedType || type || SearchFiltersType.Character) as SearchFiltersType
);

export const searchFiltersSelector = createSelector(
  globalSearchSelector,
  searchTypeSelector,
  (searchState, selectedType) => searchState[selectedType].filters
);

export const searchTermSelector = createSelector(globalSearchSelector, (searchState) => {
  return searchState.searchTerm;
});

export const lastSearchSelector = createSelector(globalSearchSelector, ({ lastSearch }) => lastSearch);

export const createSortBySelector = createSelector(
  globalSearchSelector,
  searchTypeSelector,
  (state, searchType) => state[searchType].sorted || []
);

export const clearedFiltersSelector = createSelector(globalSearchSelector, (searchState) => searchState.cleared);

export const paginationSelector = createSelector(
  featuresSelector,
  searchTypeSelector,
  globalSearchSelector,
  ({ admin_search, premium_search }, searchType, searchState) => {
    const s = searchState[searchType];

    const baseLimit = safeParseInt(s.limit, 0);
    const basePage = safeParseInt(s.page, 0);

    // Determine max limit based on search type and user privileges
    // Admin: 1000, M+ Runs: Premium 500 / Free 50, Other: Premium 50 / Free default
    const isMythicPlusRuns = searchType === SearchFiltersType.MythicPlusRuns;
    const defaultMaxLimit = admin_search
      ? 1000
      : isMythicPlusRuns
        ? premium_search
          ? 500
          : 100
        : premium_search
          ? 100
          : DEFAULT_GLOBAL_SEARCH_PAGE_SIZE;

    const limit = baseLimit && baseLimit <= defaultMaxLimit ? baseLimit : DEFAULT_GLOBAL_SEARCH_PAGE_SIZE;

    return {
      page: basePage,
      offset: basePage * limit,
      limit,
      maxLimit: defaultMaxLimit,
      isPremium: !!premium_search
    };
  }
);

// search results
export const searchResultsSelector = createSelector(
  globalSearchSelector,
  searchTypeSelector,
  (searchState, selectedType) => searchState[selectedType].results
);

export const characterResultSelector = createSelector(globalSearchSelector, ({ character: { results } }) => results);
export const guildResultSelector = createSelector(globalSearchSelector, ({ guild: { results } }) => results);
export const teamResultSelector = createSelector(globalSearchSelector, ({ team: { results } }) => results);
export const mythic_plus_runsResultSelector = createSelector(
  globalSearchSelector,
  ({ mythic_plus_runs: { results } }) => results
);
export const raid_encountersResultSelector = createSelector(
  globalSearchSelector,
  ({ raid_encounters: { results } }) => results
);

export const globalSearchTimezoneSelector = createSelector(globalSearchSelector, ({ timezone }) => timezone); // filters

export const appliedFiltersSelector = createSelector(
  globalSearchSelector,
  ({ character, guild, team, mythic_plus_runs, raid_encounters }) => ({
    characterFilters: character.filters,
    guildFilters: guild.filters,
    teamFilters: team.filters,
    mythic_plus_runsFilters: mythic_plus_runs.filters,
    raid_encountersFilters: raid_encounters.filters
  })
);

export const currentTypeAppliedFiltersSelector = createSelector(
  appliedFiltersSelector,
  searchTypeSelector,
  (appliedFilters, searchType) => {
    const key = `${searchType}Filters` as const;
    return _.values(appliedFilters[key]);
  }
);

export const statusStateSelector = createSelector(globalSearchSelector, ({ loading, loaded, error }) => ({
  loading,
  loaded,
  error
}));

export const createQuerySelector = createSelector(
  globalSearchSelector,
  searchTypeSelector,
  (state, searchType) => state[searchType].query || {}
);
